import { Injectable } from '@angular/core';
import type { GridEvent, GridEventType } from '../interfaces/grid-event.types';

export type GridEventListener<E extends GridEvent = GridEvent> = (event: E) => void;

/**
 * Mediator local de eventos entre o Host, o Bridge e os plugins.
 * Implementado com callbacks puros (Map<tipo, Set<listener>>) — sem `Subject`/`BehaviorSubject`.
 * Um Signal não modela bem eventos discretos (perderia disparos síncronos consecutivos do
 * mesmo tipo e não suporta múltiplos ouvintes independentes de forma natural), por isso o
 * barramento de eventos usa um registro de callbacks, e os signals ficam reservados para *estado*.
 */
@Injectable()
export class GridEventBus {
  private readonly listenersByType = new Map<GridEventType, Set<GridEventListener>>();

  on<K extends GridEventType>(
    type: K,
    listener: (event: Extract<GridEvent, { type: K }>) => void
  ): () => void {
    const listeners = this.listenersByType.get(type) ?? new Set<GridEventListener>();
    listeners.add(listener as GridEventListener);
    this.listenersByType.set(type, listeners);

    return () => listeners.delete(listener as GridEventListener);
  }

  publish(event: GridEvent): void {
    const listeners = this.listenersByType.get(event.type);
    listeners?.forEach(listener => listener(event));
  }

  clear(): void {
    this.listenersByType.clear();
  }
}
